import { z } from 'zod'

/**
 * A permission's id as Access Control declares it: context, resource, an optional aspect, then
 * the action, such as `access-control.roles.write`.
 */
export type PermissionId = string

/** Where the caller's own permissions are read (AC-18). */
export const myPermissionsPath = '/access-control/v1/me/permissions'

/** What the caller may do on this site. It names no record. */
export interface MyPermissions {
  /** Whether every permission passes, as for Super User, whose `permissions` is empty. */
  readonly hasAllPermissions: boolean
  /** Each permission held, with the scope kinds it is held at and, after them, the relationship scopes by name, such as `instructs`. */
  readonly permissions: ReadonlyMap<PermissionId, readonly string[]>
  /** Whether the caller may assign any role to anyone. */
  readonly mayAssignRoles: boolean
  /** The managed scope kinds the caller reaches anyone through; a grant at any other managed scope reaches no record. */
  readonly managedScopesInReach: readonly string[]
  /** Whether the caller holds the Instructor role now, whether or not they teach a class. */
  readonly isInstructor: boolean
}

/** What a caller whose permissions are not known yet, or could not be read, may do. */
export const noPermissions: MyPermissions = {
  hasAllPermissions: false,
  permissions: new Map(),
  mayAssignRoles: false,
  managedScopesInReach: [],
  isInstructor: false,
}

const myPermissionsSchema = z.object({
  hasAllPermissions: z.boolean(),
  permissions: z.array(z.object({ id: z.string(), scopes: z.array(z.string()), relationships: z.array(z.string()).default([]) })),
  mayAssignRoles: z.boolean(),
  // An API deployed before the field reaches no one through a managed scope, as far as the UI knows.
  managedScopesInReach: z.array(z.string()).default([]),
  isInstructor: z.boolean().default(false),
})

/** Narrows the API's answer; throws on anything else. */
export function parseMyPermissions(body: unknown): MyPermissions {
  const parsed = myPermissionsSchema.parse(body)
  return {
    hasAllPermissions: parsed.hasAllPermissions,
    permissions: new Map(parsed.permissions.map((permission) => [permission.id, [...permission.scopes, ...permission.relationships]])),
    mayAssignRoles: parsed.mayAssignRoles,
    managedScopesInReach: parsed.managedScopesInReach,
    isInstructor: parsed.isInstructor,
  }
}

function sameList(x: readonly string[], y: readonly string[] | undefined): boolean {
  return y !== undefined && x.length === y.length && x.every((one, index) => one === y[index])
}

/** Whether two answers say the same, so a re-read that changed nothing changes nothing on screen. */
export function samePermissions(a: MyPermissions, b: MyPermissions): boolean {
  return (
    a.hasAllPermissions === b.hasAllPermissions &&
    a.mayAssignRoles === b.mayAssignRoles &&
    sameList(a.managedScopesInReach, b.managedScopesInReach) &&
    a.permissions.size === b.permissions.size &&
    [...a.permissions].every(([id, scopes]) => sameList(scopes, b.permissions.get(id)))
  )
}

/**
 * The scope kinds that make a grant administrative: they reach other people's records. Own and
 * relationship scopes (instructs, editing delegate, created...) never do. Managed people reaches
 * only direct reports; it counts, as Sent Messages counts it, until Aaron Mulder decides otherwise
 * (story 110589): taking it out of this list is the whole change.
 */
const administrativeScopes: readonly string[] = ['allOrganizations', 'managedOrganizations', 'managedCompanies', 'managedPeople']

// A grant at one of these counts only when the caller manages someone through it.
const managedScopes: readonly string[] = ['managedOrganizations', 'managedCompanies', 'managedPeople']

function isAdministrative(mine: MyPermissions, scope: string): boolean {
  return administrativeScopes.includes(scope) && (!managedScopes.includes(scope) || mine.managedScopesInReach.includes(scope))
}

/**
 * Whether the caller holds the permission as an administrator, as the Admin menu and Admin Home
 * require: Super User, a grant with no scope, or a grant at an administrative scope that reaches
 * someone; at `scope` (or one of several) when given. Write includes Read.
 */
export function administers(mine: MyPermissions, required: PermissionId, scope?: string | readonly string[]): boolean {
  if (mine.hasAllPermissions) {
    return true
  }
  const wanted = scope === undefined ? null : typeof scope === 'string' ? [scope] : scope
  return grantsOf(mine, required).some((scopes) =>
    scopes.length === 0
      ? wanted === null
      : scopes.some((one) => isAdministrative(mine, one) && (wanted === null || wanted.includes(one))))
}

/**
 * Whether the caller holds the permission at any scope, or at `scope` when given, such as
 * `allOrganizations`, or at one of several. Write includes Read, as in the API's check.
 */
export function holds(mine: MyPermissions, required: PermissionId, scope?: string | readonly string[]): boolean {
  if (mine.hasAllPermissions) {
    return true
  }
  const wanted = scope === undefined ? null : typeof scope === 'string' ? [scope] : scope
  return grantsOf(mine, required).some((scopes) => wanted === null || wanted.some((one) => scopes.includes(one)))
}

/**
 * Whether the permission reaches every record, as the API's access filter decides: Super User, or
 * held at all organizations or with no scope. Write includes Read.
 */
export function reachesEverything(mine: MyPermissions, required: PermissionId): boolean {
  return mine.hasAllPermissions || grantsOf(mine, required).some((scopes) => scopes.length === 0 || scopes.includes('allOrganizations'))
}

/**
 * Whether the permission may reach someone else's record: held at a scope other than own. Which
 * records a managed or relationship scope reaches, the API decides. Write includes Read.
 */
export function reachesOthers(mine: MyPermissions, required: PermissionId): boolean {
  return mine.hasAllPermissions || grantsOf(mine, required).some((scopes) => scopes.length === 0 || scopes.some((scope) => scope !== 'own'))
}

// The scope kinds the permission, and its Write, are held at.
function grantsOf(mine: MyPermissions, required: PermissionId): (readonly string[])[] {
  const write = required.endsWith('.read') ? `${required.slice(0, -'read'.length)}write` : null
  return [mine.permissions.get(required), write === null ? undefined : mine.permissions.get(write)].filter(
    (scopes): scopes is readonly string[] => scopes !== undefined,
  )
}
